import { NgModule } from '@angular/core';
import { Route, RouterModule } from '@angular/router';
import { AppRoutes } from '@anecdotes/configuration-data-access';
import {
  RedirectGuard,
  AuditorPortalGuard,
} from 'core';
import { AuthCallbackComponent, AuthCallbackGuard, AuthGuardService, TokenCallbackComponent } from '@anecdotes/auth-core';
import { NoAccessComponent, RootComponent } from './components';
import { DataInitalizationCanActivate } from '@anecdotes/data-initialization';
import * as childApps from './child-apps';
import { RouteNotFoundComponent } from '@anecdotes/design-system/utils';
import { configWrapper } from '../../../environments/config';
import { NewAccountFeaturesGuard } from '@anecdotes/feature-management';
import { AuditorSimulationGuard, DefaultRedirectionGuard, FeatureFlagGuard } from './services';

const nonProductionRoutes: Route[] = [];

// We will come up with another solution once needed
if (!configWrapper.config.production) {}

const routes: Route[] = [
  {
    path: 'auth/callback',
    component: AuthCallbackComponent,
    canActivate: [AuthCallbackGuard]
  },
  {
    path: 'auth/token-callback',
    component: TokenCallbackComponent,
  },
  {
    path: 'no-access',
    component: NoAccessComponent
  },
  {
    path: '',
    component: RootComponent,
    canActivateChild: [DefaultRedirectionGuard, NewAccountFeaturesGuard, FeatureFlagGuard, AuditorSimulationGuard],
    children: [
      {
        path: '',
        children: [
          {
            path: '',
            canActivate: [AuthGuardService],
            children: [
              {
                path: '',
                data: { showNavbar: true },
                children: [
                  {
                    path: '',
                    children: [
                      /* Dashboard app */
                      {
                        path: AppRoutes.Dashboard,
                        canActivate: [DataInitalizationCanActivate],
                        data: { featureFlag: '@dashboard-page' },
                        loadChildren: () => childApps.dashboardApp.loadDashboardAsync(),
                      },
                      /* End Dashboard app */
                      { path: AppRoutes.MyControls, redirectTo: AppRoutes.EvidencePool, pathMatch: 'full' },
                      {
                        path: AppRoutes.EvidencePool,
                        loadChildren: () => childApps.evidencePoolApp.loadEvidencePoolAsync(),
                      },
                      {
                        path: AppRoutes.TrustCenter,
                        loadChildren: () => childApps.trustCenterApp.loadTrustCenterAsync(),
                      },
                      {
                        path: AppRoutes.UnifiedView,
                        canActivate: [DataInitalizationCanActivate],
                        loadChildren: () => childApps.unifiedControlsApp.loadUnifiedControlsAppAsync(),
                      },
                      {
                        path: AppRoutes.Settings,
                        canActivate: [DataInitalizationCanActivate],
                        data: { featureFlag: '@settings-page' },
                        loadChildren: () => import('./modules/settings/settings.module').then(({ SettingsModule }) => SettingsModule),
                      },
                      {
                        path: AppRoutes.PolicyManager,
                        canActivate: [DataInitalizationCanActivate],
                        loadChildren: () => childApps.policyManagerApp.loadPolicyManagerAsync(),
                      },
                    ],
                    canActivate: [AuditorPortalGuard, RedirectGuard],
                    pathMatch: 'prefix',
                  },
                ],
              },
            ],
          },
          {
            path: `${AppRoutes.ViewEvidence}/:evidence_instance_id/raw`,
            canActivate: [AuthGuardService],
            loadChildren: () =>
              import('./modules/view-evidence/view-evidence.module').then(({ ViewEvidenceModule }) => ViewEvidenceModule),
          },
          {
            path: AppRoutes.UserAccessPreview,
            canActivate: [AuthGuardService, DataInitalizationCanActivate],
            loadChildren: () => childApps.userAccessReviewApp.loadUserAccessPreviewAsync(),
          },
          {
            path: AppRoutes.UarInternalApproval,
            canActivate: [AuthGuardService, DataInitalizationCanActivate],
            loadChildren: () => childApps.userAccessReviewApp.loadUserAccessInternalAsync(),
          },
          {
            path: AppRoutes.PolicyInternalApproval,
            canActivate: [AuthGuardService, DataInitalizationCanActivate],
            loadChildren: () => childApps.policyManagerApp.loadPolicyInternalApprovalAsync(),
          },
          {
            path: AppRoutes.RiskInternalApproval,
            canActivate: [AuthGuardService, DataInitalizationCanActivate],
            loadChildren: () => childApps.riskManagementApp.loadRiskInternalApprovalAsync(),
          },
        ],
      },
      {
        path: AppRoutes.PolicyExternalApproval,
        loadChildren: () => childApps.policyManagerApp.loadPolicyExternalApprovalAsync(),
      },
      {
        path: AppRoutes.UarExternalApproval,
        loadChildren: () => childApps.userAccessReviewApp.loadUserAccessExternalAsync(),
      },
      {
        path: AppRoutes.RiskExternalApproval,
        loadChildren: () => childApps.riskManagementApp.loadRiskExternalApprovalAsync(),
      },
      {
        path: AppRoutes.SlackCollaborationExternalActions,
        loadChildren: () =>
          import('./modules/external-collaboration-approval').then(({ ExternalCollaborationApprovalModule }) => ExternalCollaborationApprovalModule),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.complianceApp.loadComplianceAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.integrationsApp.loadPluginsModuleAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.userManagementApp.loadAccessManagementAsync(),
      },
      {
        path: '',
        loadChildren: () => childApps.complianceApp.loadExternalComplianceAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.riskManagementApp.loadRiskManagementAppAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.userAccessReviewApp.loadUserAccessAppAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.integrationsApp.loadMyAnecdotesAsync(),
      },
      {
        path: '',
        data: { showNavbar: true },
        loadChildren: () => childApps.playbookApp.loadAsync(),
      },
      ...nonProductionRoutes,
      {
        path: 'redirect',
        canActivate: [AuthGuardService],
        loadChildren: () => import('@anecdotes/redirection').then(({ RedirectionModule }) => RedirectionModule)
      }
    ],
  },
  {
    path: '**',
    component: RouteNotFoundComponent,
    pathMatch: 'prefix'
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
